import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  useTenantPartnerCommission,
  useUpdateTenantPartnerCommission,
} from "../../hooks/tenant-partners-admin.hooks";

const PRESET_SHARE_PERCENTS = [0, 25, 50, 75, 100] as const;
const DEFAULT_SHARE_PERCENT = 50;

/**
 * `null`: valid, ready to save. Sim, 2026-09-28: typing 120 used to silently
 * clamp to 100 and save that — a value outside 0-100 must show an error
 * under the field and block Save instead.
 */
function validateSharePercentInput(raw: string): string | null {
  if (raw.trim() === "") {
    return "required";
  }
  const parsed = Number(raw);
  if (Number.isNaN(parsed)) {
    return "notANumber";
  }
  if (parsed < 0 || parsed > 100) {
    return "outOfRange";
  }
  return null;
}

// Rounds to 2 decimals and drops a trailing ".00"/".x0" so a whole-euro
// example reads "€40", not "€40.00" (server example is "€1000 × 8% × 50% = €40").
function roundEuros(value: number): number {
  return Math.round(value * 100) / 100;
}

interface TenantPartnerCommissionControlProps {
  tenantId: string;
}

export const TenantPartnerCommissionControl: React.FC<
  TenantPartnerCommissionControlProps
> = ({ tenantId }) => {
  const { t } = useTranslation();
  const customInputId = React.useId();
  const customInputErrorId = `${customInputId}-error`;
  const {
    data: commission,
    isLoading,
    isError,
  } = useTenantPartnerCommission(tenantId, true);
  const { mutate: saveCommission, isPending: isSaving } =
    useUpdateTenantPartnerCommission(tenantId);

  const [sharePercent, setSharePercent] = useState<number>(
    DEFAULT_SHARE_PERCENT,
  );
  const [customInput, setCustomInput] = useState<string>(
    String(DEFAULT_SHARE_PERCENT),
  );
  const [inputError, setInputError] = useState<string | null>(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  useEffect(() => {
    if (!commission) {
      return;
    }
    setSharePercent(commission.sharePercent);
    setCustomInput(String(commission.sharePercent));
    setInputError(null);
    setHasLoadedOnce(true);
  }, [commission]);

  const commissionPercent = commission?.commissionPercent ?? 0;
  const booked = commission?.example.booked ?? 0;
  const tenantEarnings = useMemo(
    () => roundEuros(booked * (commissionPercent / 100) * (sharePercent / 100)),
    [booked, commissionPercent, sharePercent],
  );

  const handlePresetSelect = (preset: number) => {
    setSharePercent(preset);
    setCustomInput(String(preset));
    setInputError(null);
  };

  const handleCustomChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    setCustomInput(raw);

    const error = validateSharePercentInput(raw);
    setInputError(error);
    if (error === null) {
      setSharePercent(Number(raw));
    }
  };

  const handleSave = () => {
    if (inputError !== null) {
      return;
    }
    saveCommission(
      { sharePercent },
      {
        onSuccess: () => {
          toast.success(t("tenants.detail.partners.commissionSaveSuccess"));
        },
        onError: () => {
          toast.error(t("tenants.detail.partners.commissionSaveError"));
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-4 rounded-xl border p-4">
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium">
          {t("tenants.detail.partners.commissionTitle")}
        </p>
        <p className="text-muted-foreground text-sm">
          {t("tenants.detail.partners.commissionDescription")}
        </p>
      </div>

      {isError ? (
        <p className="text-custom-destructive-dark text-sm">
          {t("tenants.detail.partners.commissionLoadError")}
        </p>
      ) : (
        <>
          <div className="flex flex-col gap-2">
            <span className="text-sm leading-none font-medium">
              {t("tenants.detail.partners.commissionShareLabel")}
            </span>
            <div
              role="radiogroup"
              aria-label={t("tenants.detail.partners.commissionShareLabel")}
              className="flex flex-wrap gap-2"
            >
              {PRESET_SHARE_PERCENTS.map((preset) => (
                <Button
                  key={preset}
                  type="button"
                  variant={sharePercent === preset ? "default" : "outline"}
                  size="sm"
                  role="radio"
                  aria-checked={sharePercent === preset}
                  disabled={isLoading}
                  onClick={() => handlePresetSelect(preset)}
                >
                  {preset}%
                </Button>
              ))}
            </div>

            <Input
              id={customInputId}
              type="number"
              min={0}
              max={100}
              label={t("tenants.detail.partners.commissionCustomLabel")}
              placeholder={t(
                "tenants.detail.partners.commissionCustomPlaceholder",
              )}
              value={customInput}
              onChange={handleCustomChange}
              disabled={isLoading}
              aria-invalid={inputError !== null}
              aria-describedby={
                inputError !== null ? customInputErrorId : undefined
              }
              className="max-w-[160px]"
            />
            {inputError !== null ? (
              <span
                id={customInputErrorId}
                className="text-custom-destructive-dark text-xs"
              >
                {t(
                  `tenants.detail.partners.commissionInputError.${inputError}`,
                )}
              </span>
            ) : null}
          </div>

          <p className="text-muted-foreground text-sm">
            {t("tenants.detail.partners.commissionExample", {
              booked,
              commissionPercent,
              sharePercent,
              tenantEarnings,
            })}
          </p>

          <div className="flex flex-col items-end gap-1">
            <Button
              type="button"
              size="sm"
              disabled={!hasLoadedOnce || isSaving || inputError !== null}
              onClick={handleSave}
            >
              {isSaving
                ? t("tenants.detail.partners.saving")
                : t("tenants.detail.partners.save")}
            </Button>
            {!hasLoadedOnce ? (
              <span className="text-muted-foreground text-xs">
                {t(
                  "tenants.detail.partners.commissionSaveDisabledHint",
                  "Load the commission data before saving",
                )}
              </span>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
};
